import { Injectable } from '@angular/core';

import { ViewMode } from '../utils/view-mode';
import { ApplicationStateService } from './application-state.service';
import { CommunicationService } from './communication.service';
import { ViewModeChangedEvent } from '../utils/events/view-mode-changed-event';
import { Position } from '../models/position';
import { PageService } from './page.service';
import { Page } from '../models/page';
import { environment } from '../../environments/environment';
import { sidebarConfig } from '../../environments/configs/sidebar-config';

@Injectable({
    providedIn: 'root'
})
export class PositioningService {

    /**
     * Specifies the threshold a page can shrink before it
     * is considered to be not displayable in the current view mode.
     *
     * @type {number}
     */
    private pageShrinkThreshold = 0.25;

    /**
     * Constructor
     * @param {ApplicationStateService} applicationStateService
     * @param {CommunicationService} communicationService
     * @param {PageService} pageService
     */
    constructor(private applicationStateService: ApplicationStateService,
                private communicationService: CommunicationService,
                private pageService: PageService) {
    }

    /**
     * Calculate how much space (height) is available for one page.
     *
     * @returns {number}
     */
    calculateMaxImageHeight(): number {
        const headerHeight = 70;

        return window.window.innerHeight - this.getContainerPadding().bottom - this.getContainerPadding().top - headerHeight;
    }

    /**
     * Calculate how much space (width) is available for one page in the given container.
     *
     * @returns {number}
     */
    calculateMaxImageWidth(): number {

        let maxWidth = window.window.innerWidth;

        if (!this.applicationStateService.isSidebarHidden) {
            maxWidth -= sidebarConfig.sidebarCollapsed;
        }

        if (this.applicationStateService.currentViewMode.showPageTurner) {
            // Margin for page turner
            maxWidth -= this.applicationStateService.currentViewMode.pagesToShow * 150;
        }

        return maxWidth / this.applicationStateService.currentViewMode.pagesToShow;
    }

    /**
     * Check if the given width/height fits into the container.
     * Returns which width/height to use that it fits.
     *
     * @param {number} width
     * @param {number} height
     * @returns {Position}
     */
    fitPage(width: number, height: number): Position {
        const maxWidth: number = this.calculateMaxImageWidth();
        const maxHeight: number = this.calculateMaxImageHeight();
        const aspectRatio: number = (width / height);

        if (width > maxWidth) {
            width = maxWidth;
            height = width / aspectRatio;
        }

        if (height > maxHeight) {
            height = maxHeight;
            width = height * aspectRatio;
        }

        const position: Position = new Position();
        position.w = width;
        position.h = height;

        return position;
    }

    /**
     * Get size of container for pages in given viewport.
     *
     * @returns {Position}
     */
    getPagesContainerSize(): Position {
        const containerSize: Position = new Position();
        containerSize.w = 0;
        containerSize.h = 0;

        this.pageService.getCurrentPages().forEach(page => {
            const position: Position = this.fitPage(
                page.image.width,
                page.image.height
            );

            containerSize.w += position.w;
            containerSize.h = Math.max(containerSize.h, position.h);
        });

        return containerSize;
    }

    /**
     * Determine the new view mode for the given container.
     *
     * @param {JQuery} $container
     */
    adjustViewMode($container: JQuery): void {
        const width: number = $container.width();
        const height: number = $container.height();
        let findFittingViewMode = false;

        let viewModes: Array<ViewMode> = [];

        if (this.isDefaultViewMode()) {
            viewModes = [
                ViewMode.getDefaultViewMode(),
                ViewMode.getMobileDefaultViewMode()
            ];
        }

        if (this.applicationStateService.currentViewMode.slug === 'scroll') {
            viewModes = [ViewMode.getScrollViewMode()];
        }

        for (const key in viewModes) {
            if (viewModes.hasOwnProperty(key)) {
                const viewMode: ViewMode = viewModes[key];
                if (this.fitsInViewMode(viewMode, width, height)) {
                    this.changeViewMode(viewMode);
                    findFittingViewMode = true;
                    break;
                }
            }
        }

        // If no ViewMode fits change to mobile ViewMode.
        if (!findFittingViewMode) {
            if (this.applicationStateService.currentViewMode.slug !== ViewMode.getScrollViewMode().slug) {
                this.changeViewMode(ViewMode.getMobileDefaultViewMode());
            } else {
                this.changeViewMode(ViewMode.getScrollViewMode());
            }
        }
    }

    /**
     * Check if the given width/height combination fits in the current view mode.
     *
     * @param {ViewMode} viewMode
     * @param {number} width
     * @param {number} height
     *
     * @returns {boolean}
     */
    private fitsInViewMode(viewMode: ViewMode, width: number, height: number): boolean {
        const coverPage: Page = this.pageService.getCoverPage();
        const minPageWidth: number = coverPage.image.width * this.pageShrinkThreshold;

        return (minPageWidth * viewMode.pagesToShow) <= width;
    }

    /**
     * Change the view mode.
     *
     * @param {ViewMode} viewMode
     */
    private changeViewMode(viewMode: ViewMode): void {
        if (!this.equalsCurrentViewMode(viewMode)) {
            this.applicationStateService.currentViewMode = viewMode;
            this.communicationService.announceViewModeChange(new ViewModeChangedEvent());
        }
    }

    /**
     * Check if the given view mode equals the current view mode.
     *
     * @param {ViewMode} viewMode
     * @returns {boolean}
     */
    private equalsCurrentViewMode(viewMode: ViewMode): boolean {
        return (this.applicationStateService.currentViewMode.slug === viewMode.slug);
    }

    private isDefaultViewMode(): boolean {
        const currentViewModeSlug = this.applicationStateService.currentViewMode.slug;

        return [ViewMode.getDefaultViewMode().slug, ViewMode.getMobileDefaultViewMode().slug]
            .includes(currentViewModeSlug);
    }

    /**
     * Set the padding for the pages container.
     */
    private getContainerPadding(): {
        top: number;
        right: number;
        bottom: number;
        left: number;
    } {

        if (this.applicationStateService.isScrollMode() && window.window.innerWidth > environment.mobileBreakPoint + 1) {
            return {
                top: 0,
                right: 0,
                bottom: 0,
                left: 0
            };

        }

        return {
            top: 5,
            right: 0,
            bottom: 60,
            left: 0
        };
    }
}
